import axios, { AxiosError, AxiosResponse } from "axios";
import { useSnackbar } from "material-ui-snackbar-provider";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { APIEndpoints } from "@/apis/endpoints";
import {
  setCourses,
  setDepartments,
  setIsAdmin,
  setIsPublicWebchat,
  setOnboardingCourses,
  setRunCourses,
  setUserInfos,
} from "@/pages/awareness/courses/courses-catalog/courseCatalogSlice";
import { Permission } from "@/pages/general-settings/permissions-attribution/permissions";
import { CourseList } from "@/types/awareness";
import { useUserContext } from "@/utils/contexts/UserContext";

export function useCourseCatalog(isPublicWebchat: boolean) {
  const user = useUserContext();
  const isCourseCatalogAdmin = user.permissions.includes(
    Permission.AWARENESS_COURSE_CATALOG_ADMIN,
  );
  const snackbar = useSnackbar();
  const [shouldFetch, setShouldFetch] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isAuthError, setIsAuthError] = useState(false);
  const dispatch = useDispatch();

  useEffect(
    function fetchAndPopulateResources() {
      // launch the hook only if we have a consistent state
      if (
        isCourseCatalogAdmin == null ||
        (user?.current_company?.id == null && !isPublicWebchat)
      ) {
        return;
      }
      if (!shouldFetch) {
        return;
      }
      (async () => {
        let response: AxiosResponse<CourseList>;
        try {
          if (isPublicWebchat) {
            response = await axios.get<CourseList>(
              `${APIEndpoints.LEARNING}/courses/public-courses`,
            );
          } else {
            response = await axios.get<CourseList>(
              `${APIEndpoints.LEARNING}/courses`,
            );

            const dataUserInfos = await axios.get(
              `${APIEndpoints.LEARNING}/user`,
            );
            if (isCourseCatalogAdmin) {
              const departments = await axios.get(
                `${APIEndpoints.LEARNING}/departments`,
              );
              dispatch(setDepartments(departments.data));
              dispatch(setIsAdmin(true));
            }
            dispatch(setUserInfos(dataUserInfos.data));
          }
          const courses_info: any = response.data?.courses_info;
          dispatch(setCourses(courses_info));
          dispatch(setOnboardingCourses(courses_info.slice(0, 1)));
          dispatch(setRunCourses(courses_info.slice(1)));
          dispatch(setIsPublicWebchat(isPublicWebchat));
        } catch (e) {
          if (axios.isAxiosError(e)) {
            const axiosError = e as AxiosError;
            if (axiosError.response?.status === 403) {
              setIsAuthError(true);
              setError(
                "Authentication failed. Please check your credentials and try again.",
              );
            } else {
              snackbar.showMessage("An error occurred while fetching courses");
              setError("An unexpected error occurred");
            }
          } else {
            snackbar.showMessage("An error occurred while fetching courses");
            setError("An unexpected error occurred");
          }
        } finally {
          setIsLoading(false);
        }
      })();
      setShouldFetch(false);
    },
    [
      user,
      setIsLoading,
      snackbar,
      isPublicWebchat,
      isCourseCatalogAdmin,
      shouldFetch,
      setShouldFetch,
      dispatch,
    ],
  );

  return {
    isLoading,
    error,
    isAuthError,
  };
}
